import type {
  MonetizationAcquisitionMethod,
  MonetizationAdapter,
  MonetizationAnalyticsEvent,
  MonetizationCoordinatorSnapshot,
  MonetizationEnvironment,
  MonetizationOfferId,
  MonetizationTransactionState,
} from './types.ts';

const INITIAL_MESSAGE = 'The complete free media set is ready.';

function idempotencyKey(
  offerId: MonetizationOfferId,
  method: MonetizationAcquisitionMethod,
): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `coasters:${offerId}:${method}:${random}`;
}

export class MonetizationCoordinator {
  private availability: MonetizationCoordinatorSnapshot['availability'] = {
    state: 'platform_unavailable',
    reason: 'Optional styles are unavailable.',
  };
  private wallet: MonetizationCoordinatorSnapshot['wallet'] = null;
  private offers: MonetizationCoordinatorSnapshot['offers'] = [];
  private passOwned = false;
  private readonly sessionGrants = new Set<MonetizationOfferId>();
  private selectedOfferId: MonetizationOfferId | null = null;
  private transactionState: MonetizationTransactionState = 'idle';
  private message = INITIAL_MESSAGE;
  private premiumIntentCount = 0;
  private readonly analytics: MonetizationAnalyticsEvent[] = [];
  private operationToken = 0;
  private sessionId = '';

  constructor(private readonly adapter: MonetizationAdapter) {}

  async initialize(sessionId: string): Promise<void> {
    const token = ++this.operationToken;
    this.sessionId = sessionId;
    this.transactionState = 'checking';
    const availability = await this.adapter.getAvailability();
    if (token !== this.operationToken) return;
    this.availability = availability;
    if (availability.state !== 'available') {
      this.wallet = null;
      this.offers = [];
      this.passOwned = false;
      this.sessionGrants.clear();
      this.selectedOfferId = null;
      this.transactionState = 'idle';
      this.message = availability.reason;
      return;
    }
    const [wallet, offers, restored] = await Promise.all([
      this.adapter.getWallet(),
      this.adapter.listOffers(),
      this.adapter.restoreEntitlements(),
    ]);
    if (token !== this.operationToken) return;
    this.wallet = wallet;
    this.offers = offers;
    this.passOwned = restored.durableEntitlements.includes('coaster_pass');
    this.transactionState = 'idle';
    this.message = INITIAL_MESSAGE;
  }

  resetSession(sessionId: string): void {
    ++this.operationToken;
    this.sessionId = sessionId;
    this.sessionGrants.clear();
    this.selectedOfferId = null;
    this.transactionState = 'idle';
    this.message = INITIAL_MESSAGE;
    this.premiumIntentCount = 0;
  }

  selectOffer(offerId: MonetizationOfferId | null): void {
    this.selectedOfferId = offerId;
    this.transactionState = 'idle';
    if (!offerId) {
      this.message = INITIAL_MESSAGE;
      return;
    }
    this.premiumIntentCount += 1;
    this.emitAnalytics({ name: 'premium_offer_viewed', offerId });
    const offer = this.offers.find((candidate) => candidate.id === offerId);
    this.message = offer
      ? `${offer.title} is optional. Your free version stays ready.`
      : 'That optional style is unavailable.';
  }

  denyAcquisition(offerId: MonetizationOfferId, message: string): void {
    this.selectedOfferId = offerId;
    this.transactionState = 'recoverable_error';
    this.message = message;
  }

  hasAccess(offerId: MonetizationOfferId): boolean {
    return this.passOwned || this.sessionGrants.has(offerId);
  }

  async acquire(
    offerId: MonetizationOfferId,
    method: MonetizationAcquisitionMethod,
  ): Promise<boolean> {
    if (this.transactionState === 'processing' || this.transactionState === 'checking')
      return false;
    const offer = this.offers.find((candidate) => candidate.id === offerId);
    if (!offer || this.availability.state !== 'available') {
      this.transactionState = 'recoverable_error';
      this.message = 'That optional style is unavailable. Your free version is still ready.';
      return false;
    }
    if (this.hasAccess(offerId)) {
      this.transactionState = 'granted';
      this.message = `${offer.title} is already ready for this coaster session.`;
      return true;
    }
    const token = ++this.operationToken;
    const sessionId = this.sessionId;
    this.transactionState = 'processing';
    this.message =
      method === 'credits'
        ? `Unlocking ${offer.title}…`
        : `Opening an optional rewarded ad for ${offer.title}…`;
    this.emitAnalytics({
      name: 'premium_acquisition_started',
      offerId,
      method,
    });
    const key = idempotencyKey(offerId, method);
    const result =
      method === 'credits'
        ? await this.adapter.purchase({ offerId, idempotencyKey: key })
        : await this.adapter.showRewardedAd({ offerId, idempotencyKey: key });
    if (token !== this.operationToken || sessionId !== this.sessionId) return false;
    if (!result.ok) {
      this.transactionState =
        result.code === 'cancelled'
          ? 'cancelled'
          : result.code === 'revoked'
            ? 'revoked'
            : 'recoverable_error';
      this.message = result.message;
      if ('wallet' in result && result.wallet) this.wallet = result.wallet;
      this.emitAnalytics({
        name:
          result.code === 'cancelled'
            ? 'premium_acquisition_cancelled'
            : 'premium_acquisition_failed',
        offerId,
        method,
        resultCode: result.code,
      });
      return false;
    }
    if ('wallet' in result) {
      this.wallet = result.wallet;
      if (result.durableEntitlements.includes('coaster_pass')) this.passOwned = true;
    }
    if (offerId === 'coaster_pass') this.passOwned = true;
    else this.sessionGrants.add(offerId);
    this.transactionState = 'granted';
    this.message =
      offerId === 'coaster_pass'
        ? 'Coaster Pass unlocked in this QA sandbox.'
        : `${offer.title} is ready for this coaster session.`;
    this.emitAnalytics({
      name: 'premium_acquisition_completed',
      offerId,
      method,
    });
    return true;
  }

  async restore(): Promise<void> {
    const token = ++this.operationToken;
    this.transactionState = 'checking';
    this.message = 'Checking sandbox purchases…';
    const result = await this.adapter.restoreEntitlements();
    if (token !== this.operationToken) return;
    this.passOwned = result.durableEntitlements.includes('coaster_pass');
    this.transactionState = result.ok ? 'granted' : 'recoverable_error';
    this.message = result.message;
  }

  markTreatmentApplied(offerId: MonetizationOfferId): void {
    this.emitAnalytics({ name: 'premium_treatment_applied', offerId });
  }

  snapshot(): MonetizationCoordinatorSnapshot {
    return {
      availability: this.availability,
      wallet: this.wallet,
      offers: this.offers,
      passOwned: this.passOwned,
      sessionGrants: [...this.sessionGrants],
      selectedOfferId: this.selectedOfferId,
      transactionState: this.transactionState,
      message: this.message,
      premiumIntentCount: this.premiumIntentCount,
      analytics: [...this.analytics],
    };
  }

  dispose(): void {
    ++this.operationToken;
    this.sessionGrants.clear();
    this.selectedOfferId = null;
  }

  private emitAnalytics(event: Omit<MonetizationAnalyticsEvent, 'environment'>): void {
    const environment: MonetizationEnvironment | 'unavailable' =
      this.availability.state === 'available' ? this.availability.environment : 'unavailable';
    this.analytics.push(Object.freeze({ ...event, environment }));
    if (this.analytics.length > 100) this.analytics.shift();
  }
}
